import { Link } from 'react-router-dom';
import { Building2, Users2, Globe2, Rocket, Target, Shield, MessageCircle } from 'lucide-react';
import PublicLayout from '@/components/PublicLayout';

export default function About() {
  return (
    <PublicLayout>
      <main className="pt-20 pb-14 lg:pt-28 lg:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-8 lg:p-10">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                  About Hoito
                </h1>
                <p className="text-slate-600 mt-2">
                  Empowering your financial journey
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-xl bg-slate-100 items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-700" />
              </div>
            </div>

            <div className="space-y-12">
              <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Globe2 className="w-5 h-5 text-brand-900" />
                  Our Mission
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  At Hoito, our mission is to empower everyone with simple, accessible, and intelligent financial management. We believe that managing your finances shouldn&apos;t be complex or overwhelming. By combining innovative technology with an intuitive experience, we make it easier for you to track, understand, and grow your wealth with confidence.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Users2 className="w-5 h-5 text-brand-900" />
                  Who We Are
                </h2>
                <p className="text-slate-600 mb-4 leading-relaxed">
                  Founded in 2022, Hoito is a team of passionate individuals who believe in the power of technology to transform financial management. Our diverse team brings together expertise in finance, technology, and user experience design.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  We understand that everyone&apos;s financial journey is unique, which is why we&apos;ve built a platform that adapts to your individual needs and goals.
                </p>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-brand-900" />
                  Our Values
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50 transition-all hover:shadow-md hover:border-slate-300/80">
                    <h3 className="font-semibold text-slate-900 mb-2">Transparency</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      We believe in complete transparency in everything we do, from our pricing to our security practices.
                    </p>
                  </div>
                  <div className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50 transition-all hover:shadow-md hover:border-slate-300/80">
                    <h3 className="font-semibold text-slate-900 mb-2">Innovation</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      We continuously innovate to provide you with the best tools and insights for your financial journey.
                    </p>
                  </div>
                  <div className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50 transition-all hover:shadow-md hover:border-slate-300/80">
                    <h3 className="font-semibold text-slate-900 mb-2">Security</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      Your financial security is our top priority, with bank-level encryption and privacy measures.
                    </p>
                  </div>
                  <div className="p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50 transition-all hover:shadow-md hover:border-slate-300/80">
                    <h3 className="font-semibold text-slate-900 mb-2">Empowerment</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      We empower you with knowledge and tools to take control of your financial future.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-brand-900" />
                  Our Technology
                </h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  Hoito leverages cutting-edge technology to provide you with a seamless and secure financial management experience:
                </p>
                <ul className="space-y-4">
                  <li className="flex gap-4 p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                    <div className="w-2 h-2 rounded-full bg-brand-900 mt-2 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">AI-powered insights</h4>
                      <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                        Advanced algorithms analyze your financial data to provide personalized recommendations and insights.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4 p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                    <div className="w-2 h-2 rounded-full bg-brand-900 mt-2 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Real-time analytics</h4>
                      <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                        Stay on top of your finances with real-time updates and comprehensive analytics.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-4 p-5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                    <div className="w-2 h-2 rounded-full bg-brand-900 mt-2 shrink-0" />
                    <div>
                      <h4 className="font-semibold text-slate-900">Bank-level security</h4>
                      <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                        Your data is protected with enterprise-grade encryption and security measures.
                      </p>
                    </div>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-brand-900" />
                  Our Commitment
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We&apos;re committed to helping you achieve your financial goals. Whether you&apos;re saving for a home, planning for retirement, or just want better control of your finances, Hoito is here to support you every step of the way.
                </p>
              </section>

              <div className="pt-8 border-t border-slate-200">
                <div className="flex items-center justify-center gap-2 text-center">
                  <MessageCircle className="w-5 h-5 text-brand-900" />
                  <p className="text-slate-600">
                    Need help or have feedback? Reach out at{' '}
                    <a href="mailto:hello@hoito.app" className="font-medium text-brand-900 hover:text-brand-800 transition-colors">
                      hello@hoito.app
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PublicLayout>
  );
}
